import type { Dispatch } from 'react';
import { ValidationError, PaymentMethod } from '@hotjar/webapp/billing/payment';
import { formSubmit } from '@hotjar/webapp/tracking/common';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';

import { useNotify as useDeprecatedNotify } from './useNotify';
import type { State, initialState, Action } from './Reducer';
import { ACTIONS } from './Reducer';
import { tokenizeCard } from '../../braintree/hosted-fields';

interface Account {
  id: string | number;
}

export const ValidateFields = (state: State): string[] => {
  let validationErrors: string[] = [];

  if (state.paymentType === 'credit_card') {
    type CardVerificationFieldsKey = keyof typeof initialState.verificationFields;
    const cardVerificationErrors = (
      Object.keys(state.verificationFields) as Array<CardVerificationFieldsKey>
    ).filter((key: CardVerificationFieldsKey) => !state.verificationFields[key]);
    validationErrors = validationErrors.concat(cardVerificationErrors);
  }
  return validationErrors;
};

/**
 * Track that the payment form was submited.
 *
 * @param {boolean} success Whether the form was submitted successfully.
 * @param {string} [errorMessage] The reason for submission failure.
 * @param {account} the account in question
 * @returns {undefined}
 */
export function TrackFormSubmission(
  success: boolean,
  errorMessage: string | string[] | null,
  account: Account,
): void {
  // NOTE: errorMessage param can be string[] or null too, but segment only accepts a string
  // so we take care of it in this function instead of refactoring other places too
  const failureReason = Array.isArray(errorMessage) ? errorMessage.join() : errorMessage;
  const data = {
    ...(!success && { app_form_submit_failure_reason: (failureReason ?? '') as string }),
  };

  formSubmit({
    name: 'Set payment details',
    submitSuccess: success,
    groupData: { account_id: account.id as number },
    data,
  });
}

interface InsightsError {
  error: string;
}

export function SubmitPaymentMethod({ state }: { state: State }): unknown {
  if (state.paymentType === 'credit_card') {
    return tokenizeCard({
      cardholderName: state.verificationFields.givenName,
      city: state.verificationFields.locality,
      postalCode: state.verificationFields.postalCode,
      countryCode: state.verificationFields.countryCodeAlpha2,
    });
  }
  if (state.paymentType === 'paypal' && state.isPaypalLoggedIn) {
    return Promise.resolve(new PaymentMethod(state.paypalToken.nonce, null, state.paymentType));
  }
  throw new Error(`Invalid Payment Method: ${state.paymentType}`);
}

export function OnSuccess(paymentMethod: PaymentMethod, account: Account): Promise<PaymentMethod> {
  const { success } = useDeprecatedNotify({
    when: 'onReload',
    timeout: 6000,
  });
  success('Payment method updated.');
  TrackFormSubmission(true, null, account);
  return Promise.resolve(paymentMethod);
}

export function onValidationError({
  invalidFieldKeys,
  dispatch,
  account,
}: {
  invalidFieldKeys: Array<Partial<keyof State['fieldErrors']>>;
  dispatch: Dispatch<Action>;
  account: Account;
}): void {
  const fieldErrors = Object.fromEntries(invalidFieldKeys.map((field) => [field, true]));

  dispatch({ type: ACTIONS.SET_FIELD_ERRORS, payload: { fieldErrors } });

  const validationEvents = [
    ['Invalid card billing details', ['countryCodeAlpha2', 'locality', 'givenName', 'postalCode']],
    [
      'Invalid card number, expiration date or CVV',
      ['number', 'cvv', 'expirationMonth', 'expirationYear'],
    ],
  ];

  validationEvents.some(([reason, fields]) => {
    const hasInvalidField = invalidFieldKeys.some((field) => fields.includes(field));
    if (hasInvalidField) {
      TrackFormSubmission(false, reason, account);
    }
    return hasInvalidField;
  });
}

// FIXME: Please fix this. You may refer to CFE-266 for more details.
// eslint-disable-next-line func-params-args/func-params
export function OnUpsertError(
  error: ValidationError | Error | InsightsError,
  dispatch: Dispatch<unknown>,
  account: Account,
  errorInfo: (message: string) => string,
): void {
  const { unhandledError } = useNotify();

  if (error instanceof ValidationError) {
    onValidationError({ invalidFieldKeys: error.errorFields, dispatch, account });
  } else {
    let errorMessage = null;

    if (error instanceof Error) {
      errorMessage = error.message;
    } else {
      errorMessage = error.error;
    }

    dispatch({ type: ACTIONS.ON_ERROR, payload: { error: errorInfo(errorMessage) } });

    // Notify unhandled error
    unhandledError();

    TrackFormSubmission(false, errorMessage, account);
  }
}
